import { useForm } from '@tanstack/react-form';
import { HelpCircle } from '@untitled-ui/icons-react';
import { type TFunctionNonStrict } from 'i18next';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { z } from 'zod';

import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { TextField } from '../../../../../../components/inputs/text-field';
import { ToastVariant } from '../../../../../../components/toast';
import { Tooltip } from '../../../../../../components/tooltip';
import { OrganizationIntegrationError } from '../../../../../../entity/api/organization-integration';
import { addWordpressIntegrationReqSchema } from '../../../../../../entity/api/routes/organization';
import { BadRequestError } from '../../../../../../error';
import { convertError } from '../../../../../../error/convert';
import { trackError } from '../../../../../../error/tracking';
import { captureException } from '../../../../../../logger/capture-exception';
import {
  useAddWordpressIntegration,
  useWordpressPluginDownloadUrl,
} from '../../../../../../services/storage/organization-integrations-queries';
import { tGlobal, tNamespace } from '../../../../../../services/translation';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import { parseFirstZodError } from '../../../../../../shared/validation';

const t = tNamespace('app.integrations');

const wordpressExternalUrlSchema = addWordpressIntegrationReqSchema.shape.body.shape.externalUrl;

type ConnectWordpressModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
};

export function ConnectWordpressModal(props: ConnectWordpressModalProps) {
  const { open, onOpenChange, organizationId } = props;

  const addWordpressIntegration = useAddWordpressIntegration();
  const wordpressPluginDownloadUrl = useWordpressPluginDownloadUrl();
  const addToast = useToast();

  const form = useForm({
    defaultValues: {
      externalName: '',
      externalUrl: '',
      accessKey: '',
    },
    onSubmit: function (data) {
      if (!data.formApi.state.isValid) {
        return;
      }

      addWordpressIntegration.mutate(
        {
          organizationId,
          data: {
            externalName: data.value.externalName ?? '',
            externalUrl: data.value.externalUrl ?? '',
            accessKey: data.value.accessKey ?? '',
          },
        },
        {
          onSuccess: function () {
            onOpenChange(false);
            form.reset();
            addToast({
              title: t('wordpressConnected'),
              variant: ToastVariant.Success,
              id: Date.now(),
            });
          },
          onError: function (err) {
            const handledErr = convertError(err);
            if (handledErr instanceof BadRequestError) {
              if (handledErr.message === (OrganizationIntegrationError.NotReachable as string)) {
                addToast({
                  title: t('wordpressPluginNotReachable'),
                  variant: ToastVariant.Error,
                  id: Date.now(),
                });
                return;
              }
              addToast({
                title: tGlobal('badRequest'),
                variant: ToastVariant.Error,
                id: Date.now(),
              });
              return;
            }

            captureException(
              err,
              {
                file: 'connect-wordpress-modal.tsx',
                method: 'addWordpressIntegration',
                infoMessage: 'unable to add WordPress integration',
              },
              trackError,
              addToast
            );
          },
        }
      );
    },
  });

  useEffect(
    function () {
      if (!open) {
        form.reset();
      }
    },
    [open]
  );

  function handleWordpressPluginDownload() {
    if (wordpressPluginDownloadUrl.isPending) {
      return;
    }

    wordpressPluginDownloadUrl.mutate(organizationId, {
      onSuccess: function (data: { url: string }) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      },
      onError: function (err) {
        captureException(
          err,
          {
            file: 'connect-wordpress-modal.tsx',
            method: 'getWordpressPluginDownloadUrl',
            infoMessage: 'unable to get WordPress plugin download URL',
          },
          trackError,
          addToast
        );
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="min-w-[550px]">
        <div className="mb-6 flex w-full items-center justify-center">
          <div
            className={
              'flex size-[46px] shrink-0 items-center justify-center rounded-full bg-neutral-100'
            }
          >
            <img src="/wordpress.svg" alt="WordPress" width={30} height={30} />
          </div>
        </div>
        <DialogTitle align="center">{t('connectWordpress')}</DialogTitle>
        <DialogDescription align="center">{t('connectWordpressDescription')}</DialogDescription>
        <form className="mt-8 flex w-full flex-col gap-6">
          <DialogDescription>
            <Trans
              t={t as unknown as TFunctionNonStrict}
              i18nKey="wordpressIntegrationDescription"
              components={[
                <button
                  type="button"
                  key="plugin-link"
                  className="font-inherit inline cursor-pointer border-0 bg-transparent p-0 text-branding-accent underline disabled:cursor-default disabled:opacity-60"
                  disabled={wordpressPluginDownloadUrl.isPending}
                  onClick={handleWordpressPluginDownload}
                />,
              ]}
            />
          </DialogDescription>
          <form.Field
            name="externalName"
            validators={{
              onChange: (data) =>
                parseFirstZodError(
                  addWordpressIntegrationReqSchema.shape.body.shape.externalName,
                  data.value
                ),
              onBlur: (data) =>
                parseFirstZodError(
                  addWordpressIntegrationReqSchema.shape.body.shape.externalName,
                  data.value
                ),
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('integrationName')}</p>
                      <Tooltip text={t('integrationNameTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('integrationNamePlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <form.Field
            name="externalUrl"
            validators={{
              onChange: (data) => parseFirstZodError(wordpressExternalUrlSchema, data.value),
              onBlur: (data) => parseFirstZodError(wordpressExternalUrlSchema, data.value),
              onSubmit: (data) => parseFirstZodError(wordpressExternalUrlSchema, data.value),
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('wordpressSiteUrl')}</p>
                      <Tooltip text={t('wordpressSiteUrlTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('wordpressSiteUrlPlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <form.Field
            name="accessKey"
            validators={{
              onChange: (data) =>
                parseFirstZodError(z.string().min(1, 'FIELD_REQUIRED'), data.value),
              onBlur: (data) => parseFirstZodError(z.string().min(1, 'FIELD_REQUIRED'), data.value),
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  type="password"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('applicationPassword')}</p>
                      <Tooltip text={t('applicationPasswordTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('applicationPasswordPlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
        </form>
        <DialogFooter>
          <Button
            variant="accent"
            size="xl"
            className="w-full"
            loading={addWordpressIntegration.isPending}
            onClick={() => void form.handleSubmit()}
          >
            {t('connect')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
